import { PageController } from '@client/components/_core/layout/PageController';
import { Button } from '@client/components/_ui/data-entry/ButtonModern';
import { Callout } from '@client/components/_ui/feedback/Callout';
import { InlineQueryError } from '@client/components/_ui/feedback/InlineQueryError';
import { Space } from '@client/components/_ui/layout/Space';
import { useTrpc } from '@client/hooks/api/useTrpc';
import { useFormatter } from '@client/hooks/utils/useFormatter';
import { firstQueryError, isRetryingQueries, retryFailedQueries } from '@client/trpc/query-retry';
import { CLAIM_AUTHORITY_PEOPLE_LIMIT, ENABLE_CLAIMS_AUTHORITY_FLAG } from '@shared/constants';
import { createFileRoute, Outlet } from '@tanstack/react-router';
import { RefreshCw } from 'lucide-react';
import { useState } from 'react';
import { AuthorityLevelsTable } from './AuthorityLevelsTable';
import { AuthorityPeopleTable } from './AuthorityPeopleTable';
import { countManagerWarnings, type PeopleView } from './claim-authority-people';
import { ClaimAuthorityListsLateContext, settleWithinRefreshWait } from './refresh-claim-authority-lists';

export const Route = createFileRoute('/console/$orgId/configuration/claims-products/claims/claims-authority')({
    staticData: {
        metadata: {
            title: 'Claims authority',
        },
        // Setup for reserve, payment and write-off approval (CUR-1633); authority applies once the
        // organisation has a level (CUR-2102). Organisation administrators only; the server enforces both gates.
        permissions: ['org:admin'],
        featureFlag: ENABLE_CLAIMS_AUTHORITY_FLAG,
    },
    component: RouteComponent,
});

function RouteComponent() {
    return (
        <PageController route={Route}>
            <ClaimsAuthority />
        </PageController>
    );
}

function ClaimsAuthority() {
    const { trpc } = useTrpc();
    const { formatNumber } = useFormatter();
    const [view, setView] = useState<PeopleView>('all');

    const levelsQuery = trpc.claimAuthority.listLevels.useQuery();
    const peopleQuery = trpc.claimAuthority.listPeople.useQuery();
    const queries = [levelsQuery, peopleQuery];
    const queryError = firstQueryError(queries);

    // The side panels render through the Outlet, so they stay mounted whatever the list
    // queries are doing: a failed background refetch must not unmount a form with unsaved
    // changes. The error shows beside the rows the queries still hold from their last success.
    const listError = queryError ? (
        <InlineQueryError
            error={queryError}
            onRetry={() => retryFailedQueries(queries)}
            retrying={isRetryingQueries(queries)}
        />
    ) : null;
    const hasData = levelsQuery.data !== undefined && peopleQuery.data !== undefined;

    // A change that saved before its lists refreshed (CUR-1669): the notice stays until both lists
    // have loaded since that save's refresh started. "Nothing is fetching" is not enough: a refresh
    // can pause (offline, or a retry in a background tab) or fail and keep the old rows. A failed
    // refresh shows the list error above, with its own retry, in place of the notice. The panels
    // protect themselves: they fill only from rows fetched since they opened.
    const [lateSince, setLateSince] = useState<number>();
    const listsLate =
        lateSince !== undefined && [levelsQuery, peopleQuery].some((query) => query.dataUpdatedAt < lateSince);
    // Refresh shows that it is asking again, for as long as a save would wait, then can be used again.
    const [retrying, setRetrying] = useState(false);
    const refreshLists = async () => {
        setRetrying(true);
        // Asks again; the request that has not answered is no longer waited for.
        await settleWithinRefreshWait(Promise.all(queries.map((query) => query.refetch())));
        setRetrying(false);
    };

    const people = peopleQuery.data?.rows ?? [];
    const warnings = countManagerWarnings(people);
    // The list holds the first CLAIM_AUTHORITY_PEOPLE_LIMIT active members; `total` counts them all.
    const peopleNotShown = Math.max(0, (peopleQuery.data?.total ?? 0) - people.length);

    return (
        <ClaimAuthorityListsLateContext value={setLateSince}>
            <Outlet />
            {listError && !hasData ? (
                listError
            ) : (
                <Space orientation="vertical" size={32} className="w-full max-w-7xl">
                    {listError}
                    {listsLate && !queryError && (
                        <Callout
                            variant="info"
                            role="status"
                            action={
                                <Button
                                    size="small"
                                    type="tertiary"
                                    icon={<RefreshCw />}
                                    loading={retrying}
                                    onClick={() => void refreshLists()}
                                >
                                    Refresh
                                </Button>
                            }
                        >
                            Your change is saved, but the lists are taking longer than usual to refresh and may not show
                            it yet.
                        </Callout>
                    )}
                    {peopleNotShown > 0 && (
                        <Callout
                            variant="warning"
                            title={`Showing the first ${formatNumber(CLAIM_AUTHORITY_PEOPLE_LIMIT)} of ${formatNumber(peopleQuery.data?.total)} people`}
                        >
                            The search and the views cover only the people shown. Ask for a larger list if your
                            organisation needs everyone here.
                        </Callout>
                    )}
                    {warnings > 0 && (
                        <Callout
                            variant="warning"
                            title={
                                warnings === 1
                                    ? '1 person holds a level with no active manager'
                                    : `${warnings} people hold a level with no active manager`
                            }
                        >
                            Anything they send for approval has nowhere to go. One person with no manager is the top of
                            the reporting line; check the others and set their manager.{' '}
                            <Button
                                type="link"
                                className="h-auto p-0 align-baseline"
                                onClick={() => setView('no-manager')}
                            >
                                Show them
                            </Button>
                        </Callout>
                    )}
                    {/* CUR-2102: with no switch, the first level is what turns claims authority on. */}
                    {levelsQuery.data?.rows.length === 0 && (
                        <Callout variant="info" title="Authority checks start with the first level">
                            Once a level exists, reserve changes are checked against each person’s limit, and anyone
                            without a level can’t change reserves. Give everyone who handles claims a level.
                        </Callout>
                    )}
                    <AuthorityLevelsTable levels={levelsQuery.data?.rows ?? []} loading={levelsQuery.isPending} />
                    <AuthorityPeopleTable
                        people={people}
                        loading={peopleQuery.isPending}
                        view={view}
                        onViewChange={setView}
                    />
                </Space>
            )}
        </ClaimAuthorityListsLateContext>
    );
}
